import React, {useState, useEffect} from "react";
import {Loader, Info} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient, useQuery} from '@tanstack/react-query';
import {Modal, ModalFooter, FormField, inputClass} from "./SettingsUI";

// ── Tooltip component (hover-based — no click, no label-bubbling regression) ──
const InfoTooltip = ({text}) => (
    <span
        className="relative inline-flex items-center ml-1 shrink-0 group"
        onClick={e => e.stopPropagation()} // prevent label toggle when interacting with tooltip area
    >
        <Info className="w-3.5 h-3.5 text-gray-300 group-hover:text-primary transition-colors cursor-help"/>
        {/* Popover — appears on hover via CSS group */}
        <span className="
            pointer-events-none
            absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50
            w-60 p-3 rounded-xl shadow-xl text-xs leading-relaxed
            bg-gray-900 text-gray-100
            opacity-0 group-hover:opacity-100
            scale-95 group-hover:scale-100
            transition-all duration-150
            whitespace-normal text-left
        ">
            {text}
            {/* Arrow */}
            <span
                className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"/>
        </span>
    </span>
);

// ── Main component ─────────────────────────────────────────────────────────────
const ProfileEditor = ({profile, onClose, showNotification}) => {
    const [formData, setFormData] = useState({
        name: profile ? profile.name.replace('ROLE_', '') : '',
        description: profile?.description || '',
        permissions: {}
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const queryClient = useQueryClient();

    // Fetch all available permissions
    const {data: allPermissions, isLoading: loadingPermissions} = useQuery({
        queryKey: ['permissions'],
        queryFn: async () => {
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                const headers = {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                };

                // If we already have a profile, we can extract the permissions from it
                if (profile) {
                    return profile.permissions;
                }

                // Otherwise fetch the first profile to get the list of all permissions
                const response = await api.get('/profils', {headers});
                if (response.data && response.data.length > 0) {
                    return response.data[0].permissions;
                }
                return [];
            } catch (error) {
                console.error("Error fetching permissions:", error);
                showNotification('error', "Erreur lors du chargement des permissions");
                return [];
            }
        },
        staleTime: 5 * 60 * 1000 // 5 minutes
    });

    // Initialize permissions from profile or fetch them
    useEffect(() => {
        if (profile) {
            // If editing an existing profile, use its permissions
            const permissionsMap = {};
            profile.permissions.forEach(perm => {
                permissionsMap[perm.id] = perm.status;
            });
            setFormData(prev => ({
                ...prev,
                name: profile.name.replace('ROLE_', ''),
                description: profile.description || '',
                permissions: permissionsMap
            }));
        } else if (allPermissions) {
            // If creating a new profile, initialize all permissions to false
            const permissionsMap = {};
            allPermissions.forEach(perm => {
                permissionsMap[perm.id] = false;
            });
            setFormData(prev => ({
                ...prev,
                permissions: permissionsMap
            }));
        }
    }, [profile, allPermissions]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handlePermissionChange = (permissionId, checked) => {
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [permissionId]: checked
            }
        }));
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Le nom du profil est requis";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const headers = {
                'X-account-id': accountId.id,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            };

            // Convert permissions object to array format expected by API
            // Only include permissions with status = true
            const permissionsArray = Object.entries(formData.permissions)
                .filter(([_, status]) => status === true)
                .map(([id, _]) => ({
                    id,
                    status: true
                }));

            // Prepare request data
            const payload = {
                label: formData.name,
                active: true,
                permissions: permissionsArray
            };

            if (profile) {
                // Update existing profile
                await api.put(`/profils/${profile.id}`, payload, {headers});
                showNotification('success', "Profil mis à jour avec succès");
            } else {
                // Create new profile
                await api.post('/profils', payload, {headers});
                showNotification('success', "Profil créé avec succès");
            }

            // Invalidate queries to refetch data
            queryClient.invalidateQueries({queryKey: ['profiles']});
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    // Group permissions by category — each entry has a label + help tooltip text
    const permissionGroups = {
        "CATALOGUE": {
            label: "Catalogue",
            help: "Accès au catalogue : créer, modifier et supprimer des produits, catégories et marques."
        },
        "EMPLOYEE": {
            label: "Employés",
            help: "Consulter et gérer les fiches employés (création, modification, désactivation)."
        },
        "ANNULATION": {
            label: "Annulations",
            help: "Autoriser l'annulation de lignes de vente ou de commandes en cours."
        },
        "CAISSE_PAYEMENT": {
            label: "Caisse & Paiements",
            help: "Encaisser et gérer les paiements en caisse (espèces, carte, etc.)."
        },
        "CAISSE_JUST_ADD_ITEM": {
            label: "Ajouter au Panier",
            help: "Ajouter des articles au panier sans pouvoir encaisser ni clôturer la vente."
        },
        "SETTINGS": {
            label: "Paramètres",
            help: "Accès aux paramètres du compte : taxes, modes de paiement, profils, etc."
        },
        "CLIENTS": {label: "Clients", help: "Consulter et gérer la base clients (création, modification, historique)."},
        "CALANDER": {label: "Calendrier", help: "Accès au module calendrier : réservations et rendez-vous."},
        "REMOVE_SALES": {
            label: "Supprimer des ventes",
            help: "Supprimer définitivement une vente clôturée de l'historique."
        },
        "OWEN_JOURNAL": {
            label: "Voir son propre journal",
            help: "Consulter uniquement son propre historique de ventes (journal personnel)."
        },
        "OPEN_CASH_DRAWER": {
            label: "Ouvrir le tiroir-caisse",
            help: "Déclencher l'ouverture du tiroir-caisse manuellement."
        },
        "REMOVE_ALL_BASKET": {
            label: "Supprimer le panier entier",
            help: "Vider la totalité du panier en cours d'une traite."
        },
        "EDIT_FINALIZED_SALE": {
            label: "Modifier une vente finalisée",
            help: "Modifier une vente clôturée : changer la quantité, remplacer un article ou corriger les informations de la commande."
        },
        "PENDING_SALES": {
            label: "Ventes en attente",
            help: "Mettre une vente en attente et la récupérer ultérieurement."
        },
        "HANDLE_STOCK": {
            label: "Gestion stock",
            help: "Accès à la gestion du stock depuis la caisse mobile : inventaire, fournisseurs et bons de commande."
        },
        "VIEW_PURCHASE_PRICE": {
            label: "Voir le prix d'achat",
            help: "Consulter le dernier prix d'achat (et moyenne / min / max) d'un produit depuis la fiche produit de la caisse."
        },
        "FUNDS_MOVE": {
            label: "Mouvement de fonds",
            help: "Enregistrer des entrées/sorties de fonds (fond de caisse, retrait, dépôt)."
        },
        "ROLLBACK_SALES": {
            label: "Récupération de vente",
            help: "Récupérer et rouvrir une vente précédemment mise en attente."
        },
        "SYNCH_DATA": {
            label: "Synchronisation des données",
            help: "Lancer manuellement la synchronisation des données avec le serveur."
        },
        "ACTIVITY": {
            label: "Activité",
            help: "Accès au tableau de bord d'activité : chiffre d'affaires, ventes du jour, etc."
        },
        "STATS": {label: "Statistiques", help: "Consulter les rapports et statistiques avancées de l'activité."},
        "JOURNAL_TOTAL": {
            label: "Voir total journal",
            help: "Afficher les totaux (CA TTC, HT, remises, paiements) d'un journal de caisse sans voir le détail article par article."
        },
        "JOURNAL_DETAILS": {
            label: "Voir détails journal",
            help: "Afficher le détail complet d'un journal de caisse : liste des ventes, produits vendus, moyens de paiement utilisés et historique des opérations."
        },
        "EDIT_SALE": {
            label: "Modifier la vente",
            help: "Modifier une vente en cours ou clôturée : changer la quantité, remplacer un article ou corriger les informations de la commande."
        },
        "APPLY_DISCOUNT": {
            label: "Appliquer des remises",
            help: "Appliquer manuellement une remise (en % ou montant fixe) sur un article ou sur le total de la vente."
        },
        "CHANGE_EMPLOYEE": {
            label: "Changer l'employé",
            help: "Permettre de changer l'employé assigné à une vente ou à un article du panier (utile en multi-vendeur)."
        },
        "UPDATE_PRODUCT_PRICE": {
            label: "Modifier le prix d'un article",
            help: "Autoriser la modification manuelle du prix de vente d'un article directement depuis l'écran de caisse."
        },
        "GENERATE_INVOICE": {
            label: "Générer la facture",
            help: "Autoriser la génération et le téléchargement de la facture PDF depuis le détail d'une vente."
        },
        "SET_CANAL_SALE": {
            label: "Définir le canal de vente",
            help: "Autoriser l'association ou la modification du canal de vente d'une commande ou d'une vente."
        },
        "RETURN_EXCHANGE": {
            label: "Retours et échanges",
            help: "Faire un retour ou un échange à partir d'un ticket : rembourser, émettre un avoir ou échanger des articles."
        },
        "RETURN_NO_TICKET": {
            label: "Retour sans ticket",
            help: "Retourner des articles sans ticket d'origine. Sans cette permission, le PIN d'un employé autorisé est demandé."
        },
        "RETURN_OVERRIDE": {
            label: "Retour hors délai",
            help: "Accepter le retour d'un ticket plus ancien que le délai maximum défini dans Retours > Réglages."
        },
    };

    // Get permissions to display based on profile or all permissions
    const permissionsToDisplay = profile ? profile.permissions : allPermissions || [];

    // Count active permissions for the badge
    const activeCount = Object.values(formData.permissions).filter(Boolean).length;
    const totalCount = permissionsToDisplay.length;

    return (
        <Modal
            title={profile ? "Modifier le profil" : "Nouveau profil"}
            subtitle={profile ? profile.name.replace('ROLE_', '') : "Configurer les accès"}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} form="profile-form" saving={submitting}
                                 label={profile ? "Enregistrer" : "Créer le profil"}/>}
        >
            <form id="profile-form" onSubmit={handleSubmit} className="space-y-4">
                <FormField id="name" label="Nom du profil" required error={errors.name}>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={inputClass(errors.name)}
                        placeholder="Ex: Caissier, Manager…"
                    />
                </FormField>

                <FormField id="description" label="Description">
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description || ''}
                        onChange={handleChange}
                        rows="2"
                        className={`${inputClass(false)} resize-none`}
                        placeholder="Rôle et responsabilités…"
                    />
                </FormField>

                {/* ── Permissions ── */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Permissions :</span>
                        {!loadingPermissions && totalCount > 0 && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                activeCount === 0
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                    : 'bg-primary/10 text-primary'
                            }`}>
                                {activeCount}/{totalCount} activées
                            </span>
                        )}
                    </div>

                    {loadingPermissions ? (
                        <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                            <Loader className="w-5 h-5 animate-spin text-primary"/>
                            <span className="text-xs">Chargement…</span>
                        </div>
                    ) : (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                            {Object.entries(permissionGroups).map(([key, {label: title, help}]) => {
                                const permission = permissionsToDisplay.find(p => p.label === key);
                                const isActive = permission ? (formData.permissions[permission.id] || false) : false;
                                const unavailable = !permission; // not yet in the backend list

                                return (
                                    <label
                                        key={key}
                                        className={`flex items-center justify-between px-4 py-3 transition-colors select-none ${
                                            unavailable
                                                ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-60'
                                                : isActive
                                                    ? 'bg-primary/5 cursor-pointer'
                                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
                                        }`}
                                    >
                                        <div className="min-w-0">
                                            <span className={`text-sm font-medium inline-flex items-center gap-1 flex-wrap ${
                                                isActive ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'
                                            }`}>
                                                {title}
                                                <InfoTooltip text={help}/>
                                                {unavailable && (
                                                    <span className="text-xs font-normal px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-md ml-1">
                                                        Non configuré
                                                    </span>
                                                )}
                                            </span>
                                            {permission?.description && (
                                                <span className="text-xs text-gray-500 dark:text-gray-400 block truncate">
                                                    {permission.description}
                                                </span>
                                            )}
                                        </div>

                                        {/* Toggle — disabled when not in API */}
                                        <span className="relative inline-flex shrink-0 ml-3">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={isActive}
                                                disabled={unavailable}
                                                onChange={(e) => !unavailable && handlePermissionChange(permission.id, e.target.checked)}
                                            />
                                            <span className="w-11 h-6 rounded-full bg-gray-300 dark:bg-gray-600 peer-checked:bg-primary transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2"/>
                                            <span className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"/>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
            </form>
        </Modal>
    );
};

export default ProfileEditor;
